import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneIncoming, PhoneOutgoing, PlayCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import fetchWithCsrf from '../lib/fetchWithCsrf';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PAGE_SIZE = 25;

// ─── Time helpers (same conventions as WebChatInbox) ────────────────────────────
// voice_calls timestamps already carry an explicit offset (e.g. "+00:00"), unlike
// the naive timestamps this "append Z" convention was written for elsewhere (see
// WebChatInbox.jsx) — checking only for a literal "Z" missed that case and produced
// "...+00:00Z", an unparseable date (found live 2026-09-13, showed as Invalid Date).
const HAS_TZ_INFO = /[Zz]|[+-]\d{2}:\d{2}$/;
function relativeTime(ts) {
  if (!ts) return '';
  const raw = typeof ts === 'string' && !HAS_TZ_INFO.test(ts) ? ts + 'Z' : ts;
  const d = new Date(raw);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDuration(sec) {
  if (sec === null || sec === undefined) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ─── Outcome badge ───────────────────────────────────────────────────────────────
// Derived client-side from disposition + call_analysis, not stored as its own
// column — same fields the DB already writes (retell-call-lifecycle/index.ts's
// deriveDisposition), just given a friendlier label + color here.
function deriveOutcome(call) {
  const isHot = call.call_analysis?.custom_analysis_data?.is_hot_lead === true;
  if (isHot || call.disposition === 'hot') return { label: 'Hot lead', cls: 'bg-red-50 text-red-600 ring-1 ring-red-200' };
  if (call.call_analysis?.in_voicemail) return { label: 'Voicemail', cls: 'bg-slate-50 text-slate-500 ring-1 ring-slate-200' };
  if (call.disposition === 'qualified') return { label: 'Qualified', cls: 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200' };
  if (call.disposition === 'completed') return { label: 'Completed', cls: 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200' };
  if (call.disposition === 'not_interested') return { label: 'Not interested', cls: 'bg-slate-50 text-slate-500 ring-1 ring-slate-200' };
  if (call.disposition === 'disconnected') return { label: 'Disconnected', cls: 'bg-amber-50 text-amber-600 ring-1 ring-amber-200' };
  return { label: 'Completed', cls: 'bg-slate-50 text-slate-500 ring-1 ring-slate-200' };
}

function TransferBadge({ call }) {
  if (!call.transfer_attempted_at) return null;
  const outcome = call.transfer_outcome;
  const cls = outcome === 'connected'
    ? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200'
    : outcome === 'failed'
      ? 'bg-red-50 text-red-600 ring-1 ring-red-200'
      : 'bg-amber-50 text-amber-600 ring-1 ring-amber-200';
  const label = outcome === 'connected' ? 'Transfer connected' : outcome === 'failed' ? 'Transfer failed' : 'Transfer attempted';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
      {label}
    </span>
  );
}

export default function VoiceCallsInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [calls, setCalls] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [directionFilter, setDirectionFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (directionFilter) params.set('direction', directionFilter);
      if (outcomeFilter === 'hot') params.set('disposition', 'hot');
      else if (outcomeFilter) params.set('disposition', outcomeFilter);
      const res = await fetchWithCsrf(`${API_BASE}/voice/calls?${params.toString()}`);
      if (!res.ok) {
        let detail = '';
        try {
          const body = await res.json();
          detail = body?.details || body?.error || '';
        } catch (_) {}
        throw new Error(`HTTP ${res.status}${detail ? ` — ${detail}` : ''}`);
      }
      const body = await res.json();
      setCalls(body.calls || []);
      setTotal(body.total || 0);
    } catch (err) {
      console.error('Voice calls fetch failed:', err);
      setError(`Could not load calls right now. (${err.message})`);
    } finally {
      setLoading(false);
    }
  }, [offset, directionFilter, outcomeFilter]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  // Campaign filter options derived from what's actually on the page, rather than
  // a separate endpoint — good enough for a first version of this list.
  const campaignOptions = [...new Map(
    calls.filter(c => c.campaign_id && c.campaign_name).map(c => [c.campaign_id, c.campaign_name])
  ).entries()];

  const filteredCalls = campaignFilter
    ? calls.filter(c => c.campaign_id === campaignFilter)
    : calls;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-surfox-primary" />
          <h1 className="text-lg font-semibold text-gray-900">Voice Calls</h1>
        </div>
        <button
          onClick={fetchCalls}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={directionFilter}
          onChange={(e) => { setOffset(0); setDirectionFilter(e.target.value); }}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
        >
          <option value="">All directions</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
        <select
          value={outcomeFilter}
          onChange={(e) => { setOffset(0); setOutcomeFilter(e.target.value); }}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
        >
          <option value="">All outcomes</option>
          <option value="hot">Hot lead</option>
          <option value="qualified">Qualified</option>
          <option value="completed">Completed</option>
          <option value="not_interested">Not interested</option>
          <option value="disconnected">Disconnected</option>
        </select>
        {campaignOptions.length > 1 && (
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
          >
            <option value="">All campaigns</option>
            {campaignOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-surfox-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredCalls.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">
          No calls yet. Once someone calls your voice line, it'll show up here.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {filteredCalls.map((call) => {
            const outcome = deriveOutcome(call);
            const summary = call.call_analysis?.call_summary || null;
            return (
              <div key={call.id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
              <div
                className="flex items-start gap-3 px-4 py-3"
              >
                <div className="mt-0.5">
                  {call.direction === 'outbound'
                    ? <PhoneOutgoing className="w-4 h-4 text-gray-400" />
                    : <PhoneIncoming className="w-4 h-4 text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {call.lead_id ? (
                      <button
                        onClick={() => navigate(`/lead/${call.lead_id}`)}
                        className="text-sm font-medium text-gray-900 hover:text-surfox-primary hover:underline flex items-center gap-1"
                      >
                        {call.lead_name || call.lead_phone || call.from_number || 'Unknown caller'}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    ) : (
                      <span className="text-sm font-medium text-gray-900">{call.from_number || 'Unknown caller'}</span>
                    )}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${outcome.cls}`}>
                      {outcome.label}
                    </span>
                    <TransferBadge call={call} />
                    {call.campaign_name && (
                      <span className="text-[11px] text-gray-400">{call.campaign_name}</span>
                    )}
                  </div>
                  {summary && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{summary}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0 text-right">
                  <span className="text-xs text-gray-400">{relativeTime(call.started_at)}</span>
                  <span className="text-xs text-gray-400">{formatDuration(call.duration_sec)}</span>
                  {call.recording_url && (
                    <button
                      onClick={() => setPlayingId(playingId === call.id ? null : call.id)}
                      className="flex items-center gap-1 text-xs text-surfox-primary hover:underline"
                    >
                      <PlayCircle className="w-3.5 h-3.5" /> {playingId === call.id ? 'Hide player' : 'Play'}
                    </button>
                  )}
                </div>
              </div>
              {playingId === call.id && call.recording_url && (
                <div className="px-4 pb-3">
                  {/* Inline player, not a link out — an <a href> to an audio file downloads
                      it in most browsers instead of playing it (found live 2026-09-13). */}
                  <audio controls autoPlay src={call.recording_url} className="w-full h-9">
                    Your browser doesn't support inline audio playback.
                  </audio>
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
          >
            Previous
          </button>
          <span>{offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
          <button
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
